import { useState } from 'react'

import {
  Box,
  Button,
  Icon,
  Link,
  RelativeTime,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import {
  GoalsMetric,
  GoalsMetricValueStatusEnum,
  PerformanceGoalsEmployeePageDocument,
  PerformanceGoalsMetricRowFragment,
  PerformanceGoalsUpdateQualitativeMetricDocument,
  PerformanceGoalsUpdateQuantitativeMetricDocument,
} from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'
import { useDebounceCallback } from 'usehooks-ts'

import NumberField from 'modules/core/components/NumberField'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useDeleteMetric } from 'modules/performance_goals/hooks/deleteMetric'
import {
  NormalizedQualitativeMetric,
  NormalizedQuantitativeMetric,
  normalizeMetric,
} from 'modules/performance_goals/lib/normalizeMetric'

import { MetricAssessmentStatusTag } from '../MetricAssessmentStatusTag'
import { MetricDetailsPanel } from '../MetricDetailsPanel'
import { MetricProgress } from '../MetricProgress'
import { UpdateMetricModal } from '../UpdateMetricModal'

export type UpdateQuantitativeMetricCallback = (
  id: GoalsMetric['id'],
  value: number
) => Promise<unknown>
export type UpdateQualitativeMetricCallback = (
  id: GoalsMetric['id'],
  status: GoalsMetricValueStatusEnum
) => Promise<unknown>

interface Props {
  metric: PerformanceGoalsMetricRowFragment
  refetchQueriesOnEdit?: DocumentNode[]
  showMetricGroup?: boolean
  showAssignee?: boolean
}

const QuantitativeMetricControls: React.FC<{
  metric: NormalizedQuantitativeMetric<PerformanceGoalsMetricRowFragment>
  onUpdate: UpdateQuantitativeMetricCallback
}> = ({ metric, onUpdate: onUpdateQuantitativeValue }) => {
  const { quantitativeValue } = metric
  const [displayValue, setDisplayValue] = useState(quantitativeValue.currentValue)

  const debouncedUpdate = useDebounceCallback(onUpdateQuantitativeValue, 500)

  const updateImmediately = () => {
    debouncedUpdate.cancel()
    if (displayValue === quantitativeValue.currentValue) return
    onUpdateQuantitativeValue(metric.id, displayValue)
  }

  return (
    <Box flexDirection="row" gap="s4" alignItems="center" justifyContent="flexEnd">
      <Box width="s120">
        <NumberField
          readonly={!metric._policies?.updateQuantitativeCurrentValue.can}
          max={
            quantitativeValue.startValue > quantitativeValue.targetValue
              ? quantitativeValue.startValue
              : undefined
          }
          min={
            quantitativeValue.startValue < quantitativeValue.targetValue
              ? quantitativeValue.startValue
              : undefined
          }
          value={displayValue}
          step={1}
          maxDecimals={2}
          increaseStepLabel={i18n.t('common.increase')}
          decreaseStepLabel={i18n.t('common.decrease')}
          onChange={(value) => {
            setDisplayValue(value ?? 0)
            debouncedUpdate(metric.id, value ?? 0)
          }}
          onBlur={updateImmediately}
        />
      </Box>
      <Box flexDirection="row" gap="s4">
        <Box>/</Box>
        <Box width="fit-content">{i18n.formatNumber(quantitativeValue.targetValue)}</Box>
      </Box>
    </Box>
  )
}

const QualitativeMetricControls: React.FC<{
  metric: NormalizedQualitativeMetric<PerformanceGoalsMetricRowFragment>
  onUpdate: UpdateQualitativeMetricCallback
}> = ({ metric, onUpdate }) => {
  const [updating, setUpdating] = useState(false)

  const handleUpdate = useUnsafeCallback(
    async (id: string, status: GoalsMetricValueStatusEnum) => {
      setUpdating(true)
      try {
        return await onUpdate(id, status)
      } finally {
        setUpdating(false)
      }
    },
    [onUpdate]
  )

  return (
    <Box alignItems="flexEnd">
      <TogglerCheckbox
        label={i18n.t('performance_goals.metric_row.completed')}
        disabled={updating || !metric._policies?.updateQualitativeCurrentStatus.can}
        value={metric.qualitativeValue === 'achieved'}
        onChange={(value) => handleUpdate(metric.id, value ? 'achieved' : 'not_achieved')}
      />
    </Box>
  )
}

export const MetricRow: React.FC<Props> = ({
  metric,
  refetchQueriesOnEdit,
  showMetricGroup,
  showAssignee,
}) => {
  const normalizedMetric = normalizeMetric(metric)
  const { navigation } = useNavigation()
  const agreement = metric?.performanceAgreement
  const metricGroup = metric?.metricGroup
  const assignee = metric?.assignee

  const [editing, setEditing] = useState(false)
  const [seeDetails, setSeeDetails] = useState(false)
  const { handleDelete } = useDeleteMetric()

  const [updateQuantitativeMetric] = useMutation(PerformanceGoalsUpdateQuantitativeMetricDocument, {
    extractorFn: (d) => d?.goalsMutations?.updateQuantitativeCurrentValueGoalsMetric,
    refetchQueries: [PerformanceGoalsEmployeePageDocument],
    awaitRefetchQueries: true,
  })

  const [updateQualitativeMetric] = useMutation(PerformanceGoalsUpdateQualitativeMetricDocument, {
    extractorFn: (d) => d?.goalsMutations?.updateQualitativeCurrentStatusGoalsMetric,
    refetchQueries: [PerformanceGoalsEmployeePageDocument],
    awaitRefetchQueries: true,
  })

  return (
    <>
      <Box flexDirection="row" gap="s24" alignItems="center" flexWrap="wrap" component="article">
        <Box width="s400" gap="s8">
          <Text.Normal size="300" weight="medium">
            {metric.title}
          </Text.Normal>
          <Box flexDirection="row" gap="s8" flexWrap="wrap">
            <Text.Normal size="200">
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Icon.Small icon="clock" />
                <RelativeTime date={new Date(metric.endOn)} forceRelative />
              </Box>
            </Text.Normal>
            {showMetricGroup && metricGroup && (
              <Box>
                <Link.Router
                  to={navigation.performanceGoals.groups.group.fullPath({
                    id: metricGroup.id,
                  })}
                >
                  {metricGroup.title}
                </Link.Router>
              </Box>
            )}
            {showAssignee && assignee && (
              <Box>
                {assignee.goalsEmployee ? (
                  <Link.Router
                    to={navigation.performanceGoals.employees.employee.fullPath({
                      id: assignee.goalsEmployee.id,
                    })}
                  >
                    {assignee.fullName}
                  </Link.Router>
                ) : (
                  <Text.Normal size="200" weight="medium">
                    {assignee.fullName}
                  </Text.Normal>
                )}
              </Box>
            )}
            {agreement && (
              <Box>
                <Link.Router
                  to={navigation.performanceProcess.reviews.review.target.agreement.fullPath({
                    agreementId: agreement.id,
                    reviewId: agreement.process.id,
                    targetId: agreement.target.id,
                  })}
                >
                  {agreement.managerEvaluation?.reviewerAccess &&
                    i18n.t('performance_goals.metric_row.from_agreement_with', {
                      name: agreement.managerEvaluation.reviewerAccess.fullName,
                    })}
                </Link.Router>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          flexDirection="row"
          gap="s24"
          alignItems="center"
          flexGrow
          justifyContent="flexEnd"
          flexWrap="wrap"
        >
          <Box>
            <MetricAssessmentStatusTag metric={metric} />
          </Box>
          <Box minWidth="s120" maxWidth="s300" flexGrow>
            <MetricProgress metric={metric} />
          </Box>
          <Box gap="s32" flexDirection="row" alignItems="center">
            <Box width="s140">
              {normalizedMetric.type === 'quantitative' && (
                <QuantitativeMetricControls
                  metric={normalizedMetric}
                  onUpdate={(id, value) => updateQuantitativeMetric({ variables: { id, value } })}
                />
              )}
              {normalizedMetric.type === 'qualitative' && (
                <QualitativeMetricControls
                  metric={normalizedMetric}
                  onUpdate={(id, status) => updateQualitativeMetric({ variables: { id, status } })}
                />
              )}
            </Box>
            <Box>
              <Box flexDirection="row" gap="s8">
                <Box width="s32">
                  {(metric._policies?.delete?.can || metric._policies?.update?.can) && (
                    <PopoverMenu type="list">
                      <PopoverMenu.Trigger asChild>
                        <Button iconName="ellipsis" aria-label="Options" type="secondary" />
                      </PopoverMenu.Trigger>
                      <PopoverMenu.Content side="bottom" align="end">
                        {metric._policies?.update?.can && (
                          <PopoverMenu.TouchableOption
                            title={i18n.t('performance_goals.metric_row.edit')}
                            onClick={() => setEditing(true)}
                          />
                        )}
                        {metric._policies?.delete?.can && (
                          <PopoverMenu.TouchableOption
                            title={i18n.t('performance_goals.metric_row.delete')}
                            onClick={() => handleDelete(metric)}
                            danger
                          />
                        )}
                      </PopoverMenu.Content>
                    </PopoverMenu>
                  )}
                </Box>
                <Button
                  iconName="chart-line"
                  title={i18n.t('performance_goals.metric_row.details')}
                  aria-label={i18n.t('performance_goals.metric_row.details')}
                  type="secondary"
                  onClick={() => setSeeDetails(true)}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {editing && (
        <UpdateMetricModal
          metric={normalizeMetric(metric)}
          onClose={() => setEditing(false)}
          refetchQueries={refetchQueriesOnEdit}
        />
      )}
      {seeDetails && (
        <MetricDetailsPanel metricId={metric.id} onClose={() => setSeeDetails(false)} />
      )}
    </>
  )
}
